import React, { memo, useRef, useState, useCallback, useEffect } from 'react';

function formatTime(seconds: number): string {
  if (!seconds || !isFinite(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export const VideoProgressBar: React.FC<{
  isPlaying: boolean;
  progress: number;
  duration: number;
  onTogglePlay: () => void;
  onSeek: (time: number) => void;
}> = memo(({ isPlaying, progress, duration, onTogglePlay, onSeek }) => {
  const barRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const seekFromEvent = useCallback((e: MouseEvent | React.MouseEvent) => {
    if (!barRef.current || !duration) return;
    const rect = barRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    onSeek(ratio * duration);
  }, [duration, onSeek]);

  const handleBarMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDragging(true);
    seekFromEvent(e);
  }, [seekFromEvent]);

  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      seekFromEvent(e);
    };
    const handleMouseUp = () => setIsDragging(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, seekFromEvent]);

  const progressPercent = duration > 0 ? (progress / duration) * 100 : 0;

  return (
    <div
      className="absolute bottom-0 left-0 right-0 z-20 flex items-center gap-2 px-2 py-1.5 bg-gradient-to-t from-black/80 via-black/40 to-transparent nodrag nowheel nopan"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onTogglePlay(); }}
        className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
      >
        {isPlaying ? <span className="text-[10px]">⏸</span> : <span className="text-[10px]">▶</span>}
      </button>
      <div
        ref={barRef}
        className="flex-1 h-4 flex items-center cursor-pointer relative group/bar"
        onMouseDown={handleBarMouseDown}
      >
        <div className="w-full h-1 bg-white/20 rounded-full relative">
          <div
            className="h-full bg-cyan-400 rounded-full relative"
            style={{ width: `${progressPercent}%` }}
          >
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-white rounded-full shadow transition-opacity opacity-0 group-hover/bar:opacity-100" style={isDragging ? { opacity: 1 } : undefined} />
          </div>
        </div>
      </div>
      <span className="flex-shrink-0 text-[9px] text-white/60 font-mono tabular-nums min-w-[48px] text-right">
        {formatTime(progress)} / {formatTime(duration)}
      </span>
    </div>
  );
});
